import React, { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { Document, Page, pdfjs } from 'react-pdf';
import { Button, SkeletonLoader } from '@insight-health-ai/roseate/components';
import { debounce } from 'lodash';
import {
  ChevronLeft,
  ChevronRight,
  Download01,
  Expand03,
  Minimize01,
} from '@insight-health-ai/roseate/icons';
import { useScreenSize } from '@insight-health-ai/roseate/utils';
import { downloadFile } from 'src/utils/misc';

// Set up the worker for react-pdf
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url
).toString();

// WASM URL for JPEG2000 decoding (required for OCR'd PDFs with JPX images)
// Using local node_modules path - Vite will serve these as static assets
const pdfOptions = {
  wasmUrl: `/pdfjs-wasm/`,
};

const PageType = {
  A4: 1.4142,
  LETTER: 1.2941,
} as const;

interface PDFRendererProps {
  url: string;
  name?: string;
  width?: number | 'auto';
  height?: number | 'auto';
  onLoadSuccess?: (numPages: number) => void;
  onLoadError?: (error: Error) => void;
  autoAdjust?: keyof typeof PageType | false;
  allowFullScreen?: boolean;
}

const PdfWrapper = styled.div<{ smallScreen?: boolean }>`
  height: 100%;
  width: 100%;
  overflow: hidden;
  position: relative;
  background-color: white;

  .react-pdf {
    &__Document {
      height: 100%;
      width: 100%;
      display: flex;
      justify-content: center;
      overflow: hidden auto;
    }

    &__message {
      height: 100%;
      width: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  }

  &:hover {
    .controls {
      opacity: 1;
    }
  }

  .controls {
    width: fit-content;
    margin: 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    opacity: ${({ smallScreen }) => (smallScreen ? 1 : 0)};
    position: absolute;
    top: 1rem;
    left: 0;
    right: 0;
  }
`;

const Wrapper = styled.div<{ fullScreen: boolean; smallScreen: boolean }>`
  height: 100%;
  width: 100%;
  overflow: hidden;
  display: flex;
  ${({ fullScreen }) =>
    fullScreen &&
    `
      position: fixed;
      height: 100vh;
      width: 100vw;
      top: 0;
      left: 0;
      z-index: 1010;
      background-color: #00000080;
      justify-content: center;
    `}

  ${PdfWrapper} {
    ${({ fullScreen, smallScreen }) =>
      fullScreen &&
      `
        width: ${smallScreen ? '90%' : '60%'};
        height: 100%;
        min-width: 320px;
      `}
  }
`;

const PDFRenderer: React.FC<PDFRendererProps> = ({
  url,
  width: widthProp,
  height: heightProp,
  onLoadSuccess,
  onLoadError,
  autoAdjust = false,
  allowFullScreen = true,
  name = 'document.pdf',
}) => {
  const { mediaMax } = useScreenSize();
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [fullScreen, setFullScreen] = useState<boolean>(false);

  const [wrapperDimensions, setWrapperDimensions] = useState<{
    width: number;
    height: number;
  }>({ width: 0, height: 0 });
  const wrapperRef = useRef<HTMLDivElement>(null);

  let width: number | undefined =
    typeof widthProp === 'number' && widthProp
      ? widthProp
      : wrapperDimensions.width;
  let height: number | undefined =
    typeof heightProp === 'number' && heightProp
      ? heightProp
      : wrapperDimensions.height;

  if (autoAdjust) {
    if (height / width > PageType[autoAdjust]) {
      width = wrapperDimensions.width;
      height = undefined;
    } else {
      width = wrapperDimensions.height / PageType[autoAdjust];
      height = undefined;
    }
  }

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setLoading(false);
    setError(null);
    onLoadSuccess?.(numPages);
  };

  const onDocumentLoadError = (error: Error) => {
    setLoading(false);
    setError(error.message);
    onLoadError?.(error);
  };

  const goToPrevPage = () => {
    setPageNumber((prev) => Math.max(prev - 1, 1));
  };

  const goToNextPage = () => {
    setPageNumber((prev) => Math.min(prev + 1, numPages));
  };

  // const goToPage = (page: number) => {
  //   const pageNumber = Math.min(Math.max(page, 1), numPages);
  //   setPageNumber(pageNumber);
  // };

  useEffect(() => {
    if (wrapperRef.current) {
      const debouncedResize = debounce(() => {
        if (wrapperRef.current) {
          setWrapperDimensions({
            width: wrapperRef.current.clientWidth,
            height: wrapperRef.current.clientHeight,
          });
        }
      }, 100);

      window.addEventListener('resize', debouncedResize);

      // manually call this on first render to capture the initial size as well
      setTimeout(() => {
        debouncedResize();
      }, 500);

      return () => {
        window.removeEventListener('resize', debouncedResize);
      };
    }
  }, [wrapperRef.current]);

  useEffect(() => {
    if (wrapperRef.current) {
      setWrapperDimensions({
        width: wrapperRef.current.clientWidth,
        height: wrapperRef.current.clientHeight,
      });
    }
  }, [fullScreen]);

  return (
    <div className="h-full w-full overflow-hidden flex bg-white">
      <Wrapper
        fullScreen={fullScreen && allowFullScreen}
        smallScreen={mediaMax('md')}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            setFullScreen(false);
          }
        }}
      >
        <PdfWrapper ref={wrapperRef} smallScreen={mediaMax('md')}>
          <Document
            file={url}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
            options={pdfOptions}
            loading={() => {
              return (
                <div className="h-full w-full p-4">
                  <SkeletonLoader active />
                </div>
              );
            }}
            error={() => {
              return (
                <div className="h-full w-full flex items-center justify-center p-4">
                  {error}
                </div>
              );
            }}
          >
            <Page
              pageNumber={pageNumber}
              width={width}
              height={fullScreen ? undefined : height}
              renderTextLayer={false}
              renderAnnotationLayer={false}
            />
          </Document>
          {numPages > 0 && !loading && (
            <div className="controls">
              <div className="flex items-center gap-2 bg-white shadow-lg">
                <Button
                  onClick={goToPrevPage}
                  disabled={pageNumber <= 1}
                  variant="text"
                  icon={<ChevronLeft />}
                  size="lg"
                />

                <span className="text-sm text-gray-500">
                  Page {pageNumber} of {numPages}
                </span>

                <Button
                  onClick={goToNextPage}
                  disabled={pageNumber >= numPages}
                  variant="text"
                  icon={<ChevronRight />}
                  size="lg"
                />
              </div>
              <div
                className="flex items-center bg-white shadow-lg"
                title="Download"
              >
                <Button
                  variant="text"
                  icon={<Download01 />}
                  size="lg"
                  onClick={() => downloadFile(url, name)}
                />
              </div>
              {allowFullScreen && (
                <div
                  className="flex items-center bg-white shadow-lg"
                  title={fullScreen ? 'Exit full screen' : 'Full screen'}
                >
                  <Button
                    onClick={() => setFullScreen(!fullScreen)}
                    variant="text"
                    icon={fullScreen ? <Minimize01 /> : <Expand03 />}
                    size="lg"
                  />
                </div>
              )}
            </div>
          )}
        </PdfWrapper>
      </Wrapper>
    </div>
  );
};

export default PDFRenderer;
